import { useEffect, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { upload, errorMessage } from '../lib/api';
import { CATEGORIES, CATEGORY_LABELS, type Asset, type Person } from '../lib/types';
import { Icon } from '../components/Icon';

export interface CommentDraft {
  body: string;
  category: string | null;
  attachmentIds: string[];
}

interface Props {
  projectId: string;
  people: Person[];
  placeholder: string;
  submitLabel: string;
  onSubmit: (draft: CommentDraft) => Promise<void>;
  onCancel?: () => void;
  autoFocus?: boolean;
  showCategory?: boolean;
  initialBody?: string;
  allowAttachments?: boolean;
  rows?: number;
  /** Escape still cancels; this only hides the button where space is tight. */
  hideCancelButton?: boolean;
  /** With autoFocus: focus again whenever this value changes. */
  focusKey?: string;
}

/**
 * The one text box used everywhere feedback is written: new pins, replies,
 * general discussion. Enter posts, Shift+Enter adds a line, "@" mentions a
 * teammate, and images can be pasted or attached.
 */
export function CommentInput({
  projectId,
  people,
  placeholder,
  submitLabel,
  onSubmit,
  onCancel,
  autoFocus,
  showCategory,
  initialBody = '',
  allowAttachments = true,
  rows = 2,
  hideCancelButton = false,
  focusKey,
}: Props) {
  const [body, setBody] = useState(initialBody);
  const [category, setCategory] = useState('');
  const [attachments, setAttachments] = useState<Asset[]>([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mention, setMention] = useState<{ start: number; query: string; index: number } | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) area.current?.focus({ preventScroll: true });
  }, [autoFocus, focusKey]);

  const matches = useMemo(() => {
    if (!mention) return [];
    const query = mention.query.toLowerCase();
    return people.filter((person) => person.name.toLowerCase().includes(query)).slice(0, 6);
  }, [mention, people]);

  const detectMention = (value: string, caret: number) => {
    const before = value.slice(0, caret);
    const match = /(^|\s)@([\w.-]*)$/.exec(before);
    if (match) setMention({ start: caret - match[2].length - 1, query: match[2], index: 0 });
    else setMention(null);
  };

  const pickMention = (person: Person) => {
    if (!mention || !area.current) return;
    const caret = area.current.selectionStart;
    const next = `${body.slice(0, mention.start)}@${person.name} ${body.slice(caret)}`;
    setBody(next);
    setMention(null);
    const position = mention.start + person.name.length + 2;
    requestAnimationFrame(() => {
      area.current?.focus();
      area.current?.setSelectionRange(position, position);
    });
  };

  const addFiles = async (files: File[]) => {
    if (!files.length) return;
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      for (const file of files.slice(0, 5)) form.append('files', file);
      const result = await upload<{ attachments: Asset[] }>(`/api/projects/${projectId}/attachments`, form);
      setAttachments((list) => [...list, ...result.attachments]);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setUploading(false);
    }
  };

  const onPaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    if (!allowAttachments) return;
    const files = Array.from(event.clipboardData.files);
    if (files.length && !event.clipboardData.getData('text/plain')) {
      event.preventDefault();
      void addFiles(files);
    }
  };

  const submit = async () => {
    const text = body.trim();
    if ((!text && attachments.length === 0) || busy || uploading) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit({ body: text, category: category || null, attachmentIds: attachments.map((a) => a.id) });
      setBody('');
      setAttachments([]);
      setCategory('');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (mention && matches.length) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        setMention({ ...mention, index: (mention.index + delta + matches.length) % matches.length });
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        pickMention(matches[mention.index]);
        return;
      }
      if (event.key === 'Escape') {
        event.stopPropagation();
        setMention(null);
        return;
      }
    }
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      void submit();
    } else if (event.key === 'Escape' && onCancel) {
      event.stopPropagation();
      onCancel();
    }
  };

  return (
    <div className="comment-input">
      <div className="comment-input-box">
        <textarea
          ref={area}
          className="comment-input-area"
          rows={rows}
          placeholder={placeholder}
          aria-label={placeholder}
          value={body}
          disabled={busy}
          onChange={(event) => {
            setBody(event.target.value);
            detectMention(event.target.value, event.target.selectionStart);
          }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={() => setTimeout(() => setMention(null), 120)}
        />
        {mention && matches.length > 0 && (
          <div className="mention-list" role="listbox" aria-label="Mention someone">
            {matches.map((person, index) => (
              <button
                key={person.id}
                type="button"
                role="option"
                aria-selected={index === mention.index}
                className={`menu-item ${index === mention.index ? 'is-current' : ''}`}
                onMouseDown={(event) => {
                  event.preventDefault();
                  pickMention(person);
                }}
              >
                <Icon name="at" /> {person.name}
                <span className="muted" style={{ marginLeft: 'auto', fontSize: 'var(--fs-xs)' }}>
                  {person.role === 'client' ? 'Client' : 'MK-Way'}
                </span>
              </button>
            ))}
          </div>
        )}
        {attachments.length > 0 && (
          <div className="attach-row">
            {attachments.map((asset) => (
              <span key={asset.id} className="attach-chip">
                {asset.media === 'image' ? <img src={asset.url} alt="" /> : <Icon name="file" />}
                <span className="truncate">{asset.filename}</span>
                <button type="button" className="attach-remove" aria-label={`Remove ${asset.filename}`} onClick={() => setAttachments((list) => list.filter((a) => a.id !== asset.id))}>
                  <Icon name="x" size={12} />
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="comment-input-bar">
          <div className="row" style={{ gap: 4 }}>
            {allowAttachments && (
              <>
                <input
                  ref={fileInput}
                  type="file"
                  multiple
                  hidden
                  onChange={(event) => {
                    void addFiles(Array.from(event.target.files || []));
                    event.target.value = '';
                  }}
                />
                <button type="button" className="icon-btn" aria-label="Attach a file" title="Attach a file (or paste an image)" disabled={uploading} onClick={() => fileInput.current?.click()}>
                  {uploading ? <span className="spinner" /> : <Icon name="paperclip" />}
                </button>
              </>
            )}
            {showCategory && (
              <select className="select select-inline" aria-label="Type of feedback" value={category} onChange={(event) => setCategory(event.target.value)}>
                <option value="">Type…</option>
                {CATEGORIES.map((value) => (
                  <option key={value} value={value}>
                    {CATEGORY_LABELS[value]}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div className="row" style={{ gap: 4 }}>
            {onCancel && !hideCancelButton && (
              <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>
                Cancel
              </button>
            )}
            <button type="button" className="btn btn-primary btn-sm" disabled={busy || uploading || (!body.trim() && attachments.length === 0)} onClick={() => void submit()}>
              {busy ? <span className="spinner" /> : <Icon name="send" />}
              {submitLabel}
            </button>
          </div>
        </div>
      </div>
      {error && (
        <span className="field-error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
